import React, { useState, useEffect, useCallback } from 'react';
import { TestTrend, DateRange, TrendInsight } from '../../types/trends';

interface TrendsInsightsProps {
  trendsData: Record<string, TestTrend>;
  dateRange: DateRange;
}

export const TrendsInsights: React.FC<TrendsInsightsProps> = ({
  trendsData,
  dateRange
}) => {
  const [insights, setInsights] = useState<TrendInsight[]>([]);
  const [expanded, setExpanded] = useState(true);

  const generateInsights = useCallback(() => {
    const newInsights: TrendInsight[] = [];

    Object.entries(trendsData).forEach(([testName, trend]) => {
      if (trend.dataPoints.length < 2) return;

      // Sort data points by date
      const sortedPoints = [...trend.dataPoints].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      const firstValue = sortedPoints[0].value;
      const lastValue = sortedPoints[sortedPoints.length - 1].value;
      const percentChange = ((lastValue - firstValue) / firstValue) * 100;

      // Improvement insight
      if (percentChange < -10) {
        newInsights.push({
          type: 'improvement',
          severity: 'success',
          title: `${trend.displayName} Improvement`,
          description: `Your ${trend.displayName} has decreased by ${Math.abs(percentChange).toFixed(1)}% over the selected period.`,
          affectedTests: [testName]
        });
      }

      // Concern insight
      if (percentChange > 15) {
        newInsights.push({
          type: 'concern',
          severity: 'warning',
          title: `Rising ${trend.displayName}`,
          description: `Your ${trend.displayName} has increased by ${percentChange.toFixed(1)}% over the selected period. Consider discussing this trend with your healthcare provider.`,
          affectedTests: [testName]
        });
      }

      // Out of range values
      const outOfRangeCount = sortedPoints.filter(p => p.status === 'high' || p.status === 'low').length;
      if (outOfRangeCount > sortedPoints.length * 0.5) {
        newInsights.push({
          type: 'pattern',
          severity: 'warning',
          title: `Frequent Out-of-Range ${trend.displayName}`,
          description: `More than half of your ${trend.displayName} readings are outside the normal range.`,
          affectedTests: [testName]
        });
      }

      // Stable values
      const values = sortedPoints.map(p => p.value);
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const variance = values.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / values.length;
      const stdDev = Math.sqrt(variance);
      const coefficientOfVariation = (stdDev / avg) * 100;

      if (coefficientOfVariation < 5) {
        newInsights.push({
          type: 'stable',
          severity: 'info',
          title: `Stable ${trend.displayName}`,
          description: `Your ${trend.displayName} levels have remained very consistent.`,
          affectedTests: [testName]
        });
      }
    });

    // Look for correlations between tests
    const testNames = Object.keys(trendsData);
    if (testNames.length > 1) {
      // Simple correlation check - in production, use proper statistical correlation
      const glucose = trendsData['glucose'];
      const cholesterol = trendsData['cholesterol_total'];
      
      if (glucose && cholesterol) {
        newInsights.push({
          type: 'correlation',
          severity: 'info',
          title: 'Related Markers',
          description: 'Your glucose and cholesterol levels often move together. This is common and may be influenced by diet and lifestyle factors.',
          affectedTests: ['glucose', 'cholesterol_total']
        });
      }
    }

    setInsights(newInsights);
  }, [trendsData]);

  useEffect(() => {
    generateInsights();
  }, [trendsData, dateRange, generateInsights]);

  const getInsightIcon = (type: TrendInsight['type']) => {
    switch (type) {
      case 'improvement':
        return '📈';
      case 'concern':
        return '⚠️';
      case 'stable':
        return '📊';
      case 'correlation':
        return '🔗';
      case 'pattern':
        return '🔍';
      default:
        return '💡';
    }
  };

  const getInsightColor = (severity: TrendInsight['severity']) => {
    switch (severity) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (insights.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          💡 Health Insights
        </h3>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg
            className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {expanded && (
        <>
          <div className="space-y-3">
            {insights.slice(0, 3).map((insight, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${getInsightColor(insight.severity)}`}
              >
                <div className="flex items-start">
                  <span className="text-xl mr-3">{getInsightIcon(insight.type)}</span>
                  <div className="flex-1">
                    <h4 className="font-medium">{insight.title}</h4>
                    <p className="text-sm mt-1">{insight.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};